import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaSearch, FaEye } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Event management component for administrators
 * Allows creating, editing, and managing events
 */
const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError } = useToast();
  
  // Safely stringify location for display/search
  function getLocationText(evt) {
    if (!evt || !evt.location) return '';
    if (typeof evt.location === 'string') return evt.location || '';
    if (typeof evt.location === 'object') {
      const parts = [
        evt.location.name,
        evt.location.address,
        evt.location.city,
        evt.location.country,
      ].filter(Boolean);
      return parts.join(', ');
    }
    return '';
  }

  // Organizer text for search
  function getOrganizerSearchText(evt) {
    if (!evt || !evt.organizer) return '';
    if (typeof evt.organizer === 'string') return evt.organizer;
    if (typeof evt.organizer === 'object') {
      return [
        evt.organizer.name,
        evt.organizer.email,
        evt.organizer.phone,
        evt.organizer.website,
      ]
        .filter(Boolean)
        .join(' ');
    }
    return '';
  }

  // Organizer text for display
  function getOrganizerDisplayText(evt) {
    if (!evt || !evt.organizer) return '';
    if (typeof evt.organizer === 'string') return evt.organizer;
    if (typeof evt.organizer === 'object') {
      return evt.organizer.name || evt.organizer.email || '';
    }
    return '';
  }
  
  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);
  
  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/events');
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      showError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete event
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await api.delete(`/events/${id}`);
        showSuccess('Event deleted successfully');
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
        showError('Failed to delete event');
      }
    }
  };
  
  // Filter events based on search term
  const filteredEvents = events.filter((event) => {
    const search = searchTerm.toLowerCase();
    const locationText = getLocationText(event);
    const organizerText = getOrganizerSearchText(event);
    return (
      (event.title || '').toLowerCase().includes(search) ||
      locationText.toLowerCase().includes(search) ||
      organizerText.toLowerCase().includes(search)
    );
  });
  
  // Format date to readable string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Check if event is upcoming
  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaCalendarAlt className="mr-2 text-primary" /> Event Management
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage events
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <a
            href="/admin/events/new"
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Create New Event
          </a>
        </div>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            className="form-input pl-10 py-2 w-full"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Events Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredEvents.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No events found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEvents.map((event) => (
                    <tr key={event._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {event.image ? (
                            <img
                              src={event.image}
                              alt={event.title}
                              className="h-10 w-10 rounded-md object-cover mr-3"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center mr-3">
                              <FaCalendarAlt className="text-gray-500" />
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {event.title}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(event.startDate)}</div>
                        {event.endDate && (
                          <div className="text-xs text-gray-500">
                            to {formatDate(event.endDate)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {typeof event.location === 'string' && event.location}
                          {typeof event.location === 'object' && event.location && (
                            event.location.isVirtual
                              ? 'Online Event'
                              : [event.location.name, event.location.address, event.location.city, event.location.country]
                                  .filter(Boolean)
                                  .join(', ')
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{getOrganizerDisplayText(event)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isUpcoming(event.startDate)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isUpcoming(event.startDate) ? 'Upcoming' : 'Past'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <a
                          href={`/events/${event.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 mr-3"
                          title="View"
                        >
                          <FaEye />
                        </a>
                        <a
                          href={`/admin/events/edit/${event._id}`}
                          className="text-primary hover:text-primary-600 mr-3"
                          title="Edit"
                        >
                          <FaEdit />
                        </a>
                        <button
                          onClick={() => handleDelete(event._id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventManagement;
